/**
 * WalletChangeWebSocket — DAS-81 wallet 변경 스트림 클라이언트.
 *
 * - public 채널 (인증 토큰 없음)
 * - URL: /ws/wallet-changes/
 * - 서버 push 메시지: { type: "wallet_change_update", timestamp, changes: [...] }
 * - 자동 재연결 (지수 백오프, 30초 cap)
 * - 연결 실패/끊김은 silent 처리: 60초 폴링 fallback 이 UX 회귀를 흡수.
 *
 * 안전 가드:
 *   1) WS 끊겨도 부모의 폴링은 그대로 동작 → 이 클라이언트는 부가 push 만 담당.
 *   2) onMessage 콜백은 idempotent 가정 (같은 change id 가 여러 번 와도 안전해야 함).
 *   3) DashboardWebSocket 과 달리 인증/토큰 갱신 로직 없음 → 단순화.
 *
 * DashboardWebSocket 와 별도로 둔 이유:
 *   - DashboardWebSocket 은 username + token 의존, 로그인 없이는 동작 X.
 *   - wallet_status 자체가 public 정보 → 로그인 화면이나 비공개 페이지에서도 사용 가능.
 *   - 두 stream 의 인증 모델이 달라서 한 파일에 합치면 추론이 어려워짐.
 */

class WalletChangeWebSocket {
    /**
     * @param {Function} onMessage - 서버 push 메시지를 받을 콜백 (parsed JSON)
     * @param {Function} [onStatusChange] - 'connected'|'reconnecting'|'disconnected' 상태 변경 콜백
     */
    constructor(onMessage, onStatusChange) {
        this.onMessage = onMessage;
        this.onStatusChange = onStatusChange || (() => {});

        this.ws = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;     // 짧게 — 5회 실패 후 폴링 fallback 에 의존
        this.reconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.reconnectTimer = null;
        this.shouldReconnect = true;
        this.status = 'disconnected';
    }

    connect() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }
        this._cleanup();

        const wsScheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const wsHost = process.env.REACT_APP_WS_HOST || window.location.host;
        const wsUrl = `${wsScheme}://${wsHost}/ws/wallet-changes/`;

        try {
            this.ws = new WebSocket(wsUrl);
        } catch (err) {
            console.warn('[WalletWS] construct failed, polling fallback will cover:', err);
            this._setStatus('disconnected');
            return;
        }
        this._setStatus('connecting');

        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            this._setStatus('connected');
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (this.onMessage) this.onMessage(data);
            } catch (err) {
                console.warn('[WalletWS] failed to parse message:', err);
            }
        };

        this.ws.onerror = (err) => {
            // 에러 토스트는 띄우지 않는다 — 폴링이 계속 돌고 있어 사용자에게는 영향 없음.
            // 콘솔 경고만 남김.
            console.warn('[WalletWS] connection error (polling will continue):', err);
        };

        this.ws.onclose = () => {
            this.ws = null;
            if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                this._attemptReconnect();
            } else {
                this._setStatus('disconnected');
            }
        };
    }

    disconnect() {
        this.shouldReconnect = false;
        this._cleanup();
        this._setStatus('disconnected');
    }

    isConnected() {
        return this.status === 'connected';
    }

    getStatus() {
        return this.status;
    }

    _setStatus(status) {
        this.status = status;
        try { this.onStatusChange(status, this.reconnectAttempts); } catch (_) { /* noop */ }
    }

    _attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this._setStatus('disconnected');
            return;
        }
        this._setStatus('reconnecting');
        const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(() => {
            this.connect();
        }, delay);
    }

    _cleanup() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.onerror = null;
            this.ws.onclose = null;
            try { this.ws.close(); } catch (_) { /* noop */ }
            this.ws = null;
        }
    }
}

export default WalletChangeWebSocket;
